import React, { useState, useEffect, useCallback, useMemo } from 'react';
import debounce from 'lodash/debounce';
import API from '../../../helpers/network/http';
import Loader from '../../../components/loaders/TinyLoader';
import fail_mascot from '../../../assets/images/fail_mascot.svg';
import SearchInput from '../../../components/atoms/SearchInput';
import RequestContributionModal from '../../../components/modals/RequestContributionModal';
import CommunityCard from '../../../components/atoms/cards/CommunityCard';
import { CommunityRequest } from '../../../utils/types';
import {COMMUNITY_CONTRIBUTION_PART_OPTIONS} from "../../../globals/constants/communityParts";
import Button from '../../../components/atoms/Button';
import useDynamicBreadcrumb from '../../../hooks/useDynamicBreadcrumb';
import {useI18n} from "../../../i18n";

const ExploreRequests = () => {
    const {t} = useI18n();
    const [requests, setRequests] = useState<CommunityRequest[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [showModal, setShowModal] = useState(false);

    const [filters, setFilters] = useState({
        part: '',
        search: ''
    });
    useDynamicBreadcrumb({base: t('community.breadcrumbBase'), inner: [t('community.requests.title')]});

    const fetchRequests = useCallback(async (params: Record<string, any>, reset = false) => {
        try {
            setLoading(true);
            setError(null);

            const res = await API.get(`/community-contribution-requests/explore`, { params });
            const payload = res.data?.data;
            const newData = payload?.data ?? payload?.requests ?? [];

            if (reset) setRequests(newData);
            else setRequests(prev => [...prev, ...newData]);

            const totalPages = Number(payload?.totalPages || payload?.total_pages || 1);
            const currentPage = Number(payload?.page || params.page || 1);
            setHasMore(currentPage < totalPages || newData.length === (params.limit ?? 10));
        } catch (err) {
            console.error(err);
            setError(t('community.requests.error.loadFailed'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    const debouncedFetch = useMemo(
        () =>
            debounce((filters, page) => {
                const params = { page, limit: 5, ...filters };
                fetchRequests(params, page === 1);
            }, 400),
        [fetchRequests]
    );

    useEffect(() => {
        debouncedFetch(filters, page);
        return () => debouncedFetch.cancel();
    }, [debouncedFetch, filters, page]);

    const handleLoadMore = () => {
        if (!loading && hasMore) setPage(prev => prev + 1);
    };

    const handleFilterChange = (key: string, value: string) => {
        setPage(1);
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    return (
        <div className="w-full sm:w-3/5 lg:w-3/6 mx-auto">
            <div className="flex flex-col sm:flex-row justify-between gap-3 mb-6">
                <SearchInput
                    iconPosition={'right'}
                    type="text"
                    placeholder={t('community.requests.searchBySongTitle')}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    value={filters.search}
                    className={'px-3 py-2 rounded-md bg-neutral-900 text-white w-full sm:w-3/4'}
                />
                <select
                    className="px-3 py-2 rounded-md bg-neutral-900 text-white w-full sm:w-1/4"
                    onChange={(e) => handleFilterChange('part', e.target.value)}
                    value={filters.part}
                >
                    <option value="">{t('community.requests.filters.all')}</option>
                    {COMMUNITY_CONTRIBUTION_PART_OPTIONS.map((part) => (
                        <option key={part.value} value={part.value}>{part.label}</option>
                    ))}
                </select>
            </div>

            {requests.map((req, index) => (
                <CommunityCard
                    key={`${req?.request_id || req?.group_id || index}`}
                    community_entry={req as any}
                    isRequest
                    showUpvotes
                />
            ))}

            {loading && (
                <div className="flex justify-center mt-10">
                    <Loader className="h-auto w-auto" />
                </div>
            )}

            {!loading && error && (
                <div className="flex flex-col justify-center items-center mt-10">
                    <img src={fail_mascot} alt={t('community.requests.error.alt')} width={200} />
                    <p className="text-red-400 mt-4">{error}</p>
                </div>
            )}

            {!loading && !error && !requests.length && (
                <div className="flex flex-col justify-center items-center mt-10">
                    <img src={fail_mascot} alt={t('community.requests.empty.alt')} width={200} />
                    <p className="text-white opacity-70 mt-4 mb-4 text-center">
                        {t('community.requests.empty.description')}<br />
                        {t('community.requests.empty.prompt')}
                    </p>
                    <Button
                        title={t('community.requests.empty.cta')}
                        onClick={() => setShowModal(true)}
                    />
                </div>
            )}

            {!loading && !error && hasMore && (
                <div className="flex justify-center mt-6">
                    <button
                        className="px-6 py-2 bg-neutral-900 text-white rounded hover:bg-neutral-600"
                        onClick={handleLoadMore}
                    >
                        {t('community.requests.loadMore')}
                    </button>
                </div>
            )}

            <RequestContributionModal
                show={showModal}
                onClose={() => setShowModal(false)}
            />
        </div>
    );
};

export default ExploreRequests;
